import React, { useCallback, useEffect, useRef, useState, MouseEvent } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Popover from '@mui/material/Popover';
import MenuList from '@mui/material/MenuList';
import MenuItem from '@mui/material/MenuItem';
import { useNavigate } from 'react-router-dom';
import { useSources } from '../context/SourcesContext';
import { useDialog } from '../context/DialogContext';
import { useToast } from '../context/ToastContext';
import AddSourceDialog from '../components/dialogs/AddSourceDialog';
import ArticleSummaryPanel from '../components/articles/ArticleSummaryPanel';
import { exportSummaryPdf } from '../lib/exportSummaryPdf';
import { FeedSortBy, FeedSortDir } from '../lib/feedRequest';
import { ArticleSummary, FeedArticle, Source } from '../types';
import {
  ArticleCard,
  Button,
  DateRangePicker,
  EmptyState,
  FilterButton,
  PageLoader,
  SearchInput,
  SidePanel,
  Plus,
  Spinner,
} from '../uikit';
import {
  FeedFetchKind,
  SORT_OPTIONS,
  canFetchNextPage,
  feedReducer,
  findSortOption,
  formatFeedArticleDate,
  initialFeedState,
  isFeedDisconnectError,
} from './allArticlesFeed';
import {
  DEFAULT_FEED_FILTERS,
  FeedFiltersState,
  dateFilterActive,
  isDefaultFilters,
  reconcileSourceIdsOnSourcesGrowth,
  sourcesFilterActive,
  toFeedParams,
} from './feedFilters';
import { SourcesFilter } from './SourcesFilter';
import { ScanIndicator } from './ScanIndicator';

const PAGE_SIZE = 20;

// Module-scope (not a component closure) so its identity never changes and
// it never needs to appear in a dependency array.
function buildFeedParams(
  offset: number,
  sortBy: FeedSortBy,
  sortDir: FeedSortDir,
  search: string,
  filters: FeedFiltersState,
  activeSourceIds: string[]
) {
  return {
    limit: PAGE_SIZE,
    offset,
    sortBy,
    sortDir,
    ...(search ? { search } : {}),
    ...toFeedParams(filters, activeSourceIds),
  };
}

interface SortMenuProps {
  sortBy: FeedSortBy;
  sortDir: FeedSortDir;
  onChange: (sortBy: FeedSortBy, sortDir: FeedSortDir) => void;
}

// Figma 2871:49860's sort trigger: a FilterButton opening a Popover/MenuList,
// same composition as the kit's own DateRangePicker (FilterButton.tsx /
// DateRangePicker.tsx) - not extracted into the shared kit itself since this
// exact 4-option list (Newest/Oldest/A-Z/Z-A) is specific to the articles
// feed, not a general-purpose control.
function SortMenu({ sortBy, sortDir, onChange }: SortMenuProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = (e: MouseEvent<HTMLElement>) => setAnchorEl(e.currentTarget);
  const close = () => setAnchorEl(null);

  const current = findSortOption(sortBy, sortDir) ?? SORT_OPTIONS[0];
  const isDefault = current === SORT_OPTIONS[0];

  return (
    <>
      <FilterButton label={current.label} active={!isDefault} onClick={open} />
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        PaperProps={{ sx: { p: 1, minWidth: 180 } }}
      >
        <MenuList dense>
          {SORT_OPTIONS.map((option) => (
            <MenuItem
              key={option.label}
              selected={option === current}
              onClick={() => {
                onChange(option.sortBy, option.sortDir);
                close();
              }}
              sx={{ fontSize: 14, borderRadius: '6px' }}
            >
              {option.label}
            </MenuItem>
          ))}
        </MenuList>
      </Popover>
    </>
  );
}

interface FetchLogEntry {
  kind: FeedFetchKind;
  offset: number;
  sortBy: FeedSortBy;
  sortDir: FeedSortDir;
  search: string;
  filters: FeedFiltersState;
}

// COM-638 task 4: the All Articles main page (Figma 2871:49860). Replaces
// SourcesSidebar + ContentArea + CategoriesSidebar as the app's "/" route.
const AllArticlesPage: React.FC = () => {
  const {
    fetchArticlesFeed,
    getArticleSummary,
    canManageProject,
    selectedProject,
    markArticleRead,
    scanningSourceCount,
    sourceScanCompletedCount,
  } = useSources();
  const { openAddDialog } = useDialog();
  // COM-647 Task 4: the Sources filter dropdown's "Sources Settings" footer
  // button (SourcesFilter.tsx) navigates through a plain callback prop, not
  // its own `useNavigate()` call - see that component's own doc comment on
  // `onOpenSourcesSettings` for why.
  const navigate = useNavigate();
  // COM-647 Task 3: moved onto the shared toast host (ToastContext.tsx) -
  // `showToast` with no `autoHideMs` preserves this page's existing
  // "persists until dismissed" behavior for a feed-fetch error.
  const { showToast } = useToast();

  const [state, dispatch] = React.useReducer(feedReducer, initialFeedState);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [sort, setSort] = useState<{ sortBy: FeedSortBy; sortDir: FeedSortDir }>({
    sortBy: SORT_OPTIONS[0].sortBy,
    sortDir: SORT_OPTIONS[0].sortDir,
  });

  // COM-639: Sources/Date toolbar filters (Figma 2871:52928/2871:54178) -
  // see feedFilters.ts for the full "default != all selected" model. Any
  // change to this state resets the feed the same way a search/sort change
  // does (see the effect below), via the SAME runFetch/reset path.
  const [filters, setFilters] = useState<FeedFiltersState>(DEFAULT_FEED_FILTERS);
  // The Sources filter dropdown's own lazily-loaded, page-lifetime-cached
  // active source list (SourcesFilter.tsx), mirrored up here because
  // feedFilters.ts's set-comparison helpers (`isAllSourcesSelected` et al)
  // need it too - both for the `active`-prop calculations below AND for
  // `toFeedParams` inside `runFetch`. Kept in a ref as well (see
  // `activeSourceIdsRef` below) so reading its latest value never has to
  // appear in an effect's dependency array - the dropdown finishing its
  // OWN first load is not itself a "filter change" that should refetch the
  // feed.
  const [activeSourceIds, setActiveSourceIds] = useState<string[]>([]);
  const activeSourceIdsRef = useRef(activeSourceIds);
  activeSourceIdsRef.current = activeSourceIds;

  const [summaryPanelOpen, setSummaryPanelOpen] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryData, setSummaryData] = useState<ArticleSummary | null>(null);
  const [activeArticle, setActiveArticle] = useState<FeedArticle | null>(null);

  // `fetchArticlesFeed`/`getArticleSummary` are plain functions on
  // SourcesContext's value (not `useCallback`-wrapped there), so their
  // identity changes on every SourcesProvider render - unrelated re-renders
  // (e.g. any socket event) would otherwise retrigger every effect/callback
  // that lists them as a dependency. Refs updated every render sidestep that
  // without needing the functions in any dependency array.
  const fetchArticlesFeedRef = useRef(fetchArticlesFeed);
  fetchArticlesFeedRef.current = fetchArticlesFeed;
  const getArticleSummaryRef = useRef(getArticleSummary);
  getArticleSummaryRef.current = getArticleSummary;
  const markArticleReadRef = useRef(markArticleRead);
  markArticleReadRef.current = markArticleRead;

  const requestSeqRef = useRef(0);
  const lastRequestRef = useRef<FetchLogEntry | null>(null);

  const runFetch = useCallback(
    (kind: FeedFetchKind, offset: number, sortBy: FeedSortBy, sortDir: FeedSortDir, search: string, filters: FeedFiltersState) => {
      const seq = ++requestSeqRef.current;
      lastRequestRef.current = { kind, offset, sortBy, sortDir, search, filters };
      dispatch({ type: 'fetchStart', kind });

      // `activeSourceIdsRef.current`, not the `activeSourceIds` state
      // variable: `runFetch`'s own identity is stable (`[]` deps below), so
      // closing over the state directly would freeze it at whatever it was
      // when this callback was first created - the ref always reads
      // whatever the Sources dropdown most recently loaded.
      fetchArticlesFeedRef.current(buildFeedParams(offset, sortBy, sortDir, search, filters, activeSourceIdsRef.current)).then(
        (page) => {
          if (requestSeqRef.current !== seq) return; // superseded by a newer request
          dispatch({ type: 'fetchSuccess', kind, page });
        },
        (err: unknown) => {
          if (requestSeqRef.current !== seq) return;
          if (isFeedDisconnectError(err)) {
            dispatch({ type: 'fetchDisconnected', kind });
            return;
          }
          const message = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
          dispatch({ type: 'fetchError', kind, message });
          // No `autoHideMs`: persists until dismissed, same as before this
          // moved onto the shared host.
          showToast(message, 'error');
        }
      );
    },
    // `showToast` (ToastContext) is `useCallback`-memoized with a stable
    // dependency array, so including it here doesn't retrigger this
    // callback's identity on unrelated ToastProvider renders - same
    // reasoning `dispatchScan` gets elsewhere in this codebase.
    [showToast]
  );

  // Debounce the raw input ~300ms before it becomes a query param.
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchInput.trim()), 300);
    return () => clearTimeout(timer);
  }, [searchInput]);

  // First load, and every search/sort/FILTER change thereafter: reset to
  // offset 0 and replace the list. `hasMountedRef` is what tells the two
  // apart - only the very first run should show the full-page PageLoader.
  //
  // `filters` is listed directly (not some derived primitive) - every
  // mutation path (`setFilters`) always produces a brand-new object, so a
  // reference-equality dependency is exactly "did the filters change",
  // which is exactly what should reset the feed (COM-639 task 3 brief:
  // "any filter change resets offset 0 and replaces the list"). Loading the
  // Sources dropdown's own active-source list (`activeSourceIds`) is
  // deliberately NOT a dependency here - see `activeSourceIdsRef`'s comment
  // above: that list finishing its first load is not itself a filter
  // change a user made.
  //
  // This deliberately waits for `selectedProject` rather than firing on
  // mount unconditionally: `fetchArticlesFeed` resolves immediately with an
  // empty page when no project is selected yet (SourcesContext.tsx), which
  // is true on every real load - project selection is itself a socket
  // round-trip that hasn't necessarily completed by the time this page
  // mounts. Without this dependency, that first empty reply would stick as
  // the permanent "No articles yet" state instead of ever being retried once
  // a project becomes available. `selectedProject`'s reference also changes
  // on an unrelated project-data update (e.g. a GPT model change from
  // another tab - see SourcesContext's `projectDataUpdated` handler), which
  // causes one harmless extra 'reset' fetch; same tradeoff SettingsPage.tsx
  // already accepts for its own `selectedProject`-keyed effect.
  const hasMountedRef = useRef(false);
  useEffect(() => {
    if (!selectedProject) return;
    const kind: FeedFetchKind = hasMountedRef.current ? 'reset' : 'initial';
    hasMountedRef.current = true;
    runFetch(kind, 0, sort.sortBy, sort.sortDir, debouncedSearch, filters);
  }, [selectedProject, debouncedSearch, sort.sortBy, sort.sortDir, filters, runFetch]);

  // COM-640: catches the feed up once the user's OWN add-source scan
  // finishes - `sourceScanCompletedCount` (SourcesContext.tsx) only bumps
  // on the `sourcesLoaded` that follows a successful `sourceAdded`, never on
  // a failure/timeout, so this never fires for a refused add. Runs the SAME
  // `runFetch('reset', ...)` path a filter change uses, so `requestSeqRef`
  // supersession above already covers a completion landing mid-scroll.
  //
  // Compared against a ref seeded with the count's own starting value
  // (not a plain "run once after mount" flag) so the effect can stay keyed
  // on `sourceScanCompletedCount` itself: on mount the ref already equals
  // the current count, so the very first run is always a no-op, exactly
  // like `hasMountedRef` distinguishes 'initial' from 'reset' above.
  const previousScanCompletedCountRef = useRef(sourceScanCompletedCount);
  useEffect(() => {
    if (sourceScanCompletedCount === previousScanCompletedCountRef.current) {
      return;
    }
    previousScanCompletedCountRef.current = sourceScanCompletedCount;
    runFetch('reset', 0, sort.sortBy, sort.sortDir, debouncedSearch, filters);
  }, [sourceScanCompletedCount, sort.sortBy, sort.sortDir, debouncedSearch, filters, runFetch]);

  const handleRetry = useCallback(() => {
    const last = lastRequestRef.current;
    if (!last) return;
    runFetch(last.kind, last.offset, last.sortBy, last.sortDir, last.search, last.filters);
  }, [runFetch]);

  // Endless scroll: append the next page once the sentinel below the grid
  // becomes visible. Guarded the same way DataTable's own sentinel is - only
  // when nothing is already in flight and more rows remain than are loaded.
  // Carries the SAME `filters` (and, via `runFetch`'s own
  // `activeSourceIdsRef` read, the same active-source list) as the initial/
  // reset fetch that built the currently-loaded page - an append with
  // different params than the page it's extending would silently corrupt
  // the list (e.g. rows the current filter would exclude appended after
  // ones it wouldn't).
  const onEndReached = useCallback(() => {
    if (!canFetchNextPage(state)) return;
    runFetch('append', state.articles.length, sort.sortBy, sort.sortDir, debouncedSearch, filters);
  }, [state, sort.sortBy, sort.sortDir, debouncedSearch, filters, runFetch]);

  // Callback ref (same approach as DataTable.tsx's sentinel - deliberately
  // not reused directly, this is a card grid, not a table) so the observer
  // re-attaches whenever `onEndReached`'s identity changes, including the
  // empty-state -> populated-grid transition where the sentinel doesn't
  // exist on the first render.
  const observerRef = useRef<IntersectionObserver | null>(null);
  const sentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;
      if (!node) return;
      const observer = new IntersectionObserver((entries) => {
        if (entries.some((e) => e.isIntersecting)) onEndReached();
      });
      observer.observe(node);
      observerRef.current = observer;
    },
    [onEndReached]
  );

  const handleClearSearch = () => {
    setSearchInput('');
    setDebouncedSearch('');
  };

  // COM-639: resets FILTERS ONLY - NOT the search box. The two "Clear"
  // actions are deliberately separate: search already has its own
  // dedicated empty state + "Clear search" action below (the
  // `debouncedSearch` branch in `renderBody`), and this button lives in a
  // DIFFERENT empty state (the filters-deviate one) that renders instead of
  // that search branch when filters deviate - see the priority order in
  // `renderBody`. Touching `searchInput`/`debouncedSearch` here would also
  // silently discard a search term the user typed on purpose while only
  // meaning to clear the filters.
  const handleClearFilters = () => {
    setFilters(DEFAULT_FEED_FILTERS);
  };

  // Whole-branch review fix wave (Important 4): keeps the Sources
  // `FilterButton`'s "filtered" state and the dropdown's own checkboxes from
  // silently disagreeing with each other when a source is added mid-session
  // - see `reconcileSourceIdsOnSourcesGrowth`'s own doc comment
  // (feedFilters.ts) for the full reachable scenario and why collapsing to
  // `null` is scoped to exactly the "was already all selected" case.
  // `activeSourceIdsRef.current` (read here BEFORE `setActiveSourceIds`
  // below updates it, safe since this callback runs synchronously) is the
  // list this dropdown had BEFORE the newly added source joined it - the
  // "previous" half of that function's comparison.
  //
  // This does cause one extra harmless 'reset' fetch when it fires (`filters`
  // changing by reference re-triggers the effect above) - the exact same
  // tradeoff already accepted for an unrelated `selectedProject` update (see
  // that effect's own comment) and for SettingsPage.tsx's analogous case:
  // `toFeedParams` sends no `sourceIds` either way (before or after this
  // collapse), so the refetch returns the same page, just fetched twice.
  const handleSourcesLoaded = (sources: Source[]) => {
    setFilters((prevFilters) => {
      const nextSourceIds = reconcileSourceIdsOnSourcesGrowth(prevFilters.sourceIds, activeSourceIdsRef.current);
      return nextSourceIds === prevFilters.sourceIds ? prevFilters : { ...prevFilters, sourceIds: nextSourceIds };
    });
    setActiveSourceIds(sources.map((source) => source.id));
  };

  const handleViewSummary = (article: FeedArticle) => {
    setActiveArticle(article);
    setSummaryPanelOpen(true);
    setSummaryLoading(true);
    setSummaryData(null);

    // Restores the read/unread flip the old ContentArea drove via
    // `updateArticleStatus` on open (COM-638 fix - a regression Task 4
    // introduced, not a deferral: COM-641 still owns the full read/unread
    // REDESIGN, this only restores parity with the old screen's live
    // behaviour). Only ever clears the badge, never sets it back to new, so
    // this only fires for an article that's currently `isNew`. The dispatch
    // is optimistic - it flips the local card immediately - alongside the
    // fire-and-forget server emit; a daily refetch reconciles the rare case
    // where the emit itself is lost.
    if (article.isNew) {
      dispatch({ type: 'markRead', url: article.url });
      markArticleReadRef.current(article.sourceId, article.url);
    }

    getArticleSummaryRef.current(article.url).then((data) => {
      setSummaryData(data);
      setSummaryLoading(false);
    });
  };

  const handleCloseSummary = () => {
    setSummaryPanelOpen(false);
  };

  const handleExportPdf = () => {
    exportSummaryPdf({
      articleTitle: activeArticle?.title || '',
      articleUrl: activeArticle?.url || '',
      summary: summaryData,
      updateDateFallback: activeArticle?.updateDate ?? null,
    });
  };

  // `canManageProject` is a plain function on SourcesContext's value (a new
  // identity every render, same as `fetchArticlesFeed`/`getArticleSummary`
  // above) - wrapping its call in `useMemo` keyed on that identity recomputed
  // on every single render anyway, so it bought nothing over calling it
  // directly (nit, review round 3).
  const canAddSource = canManageProject();

  const addSourceButton = canAddSource ? (
    <Button variant="primary" startIcon={<Plus sx={{ width: 16, height: 16 }} />} onClick={openAddDialog}>
      Add new source
    </Button>
  ) : undefined;

  const renderBody = () => {
    if (state.status === 'loading-initial') {
      return <PageLoader />;
    }

    if (state.status === 'disconnected' && state.articles.length === 0) {
      return (
        <EmptyState
          title="Connection lost"
          description="We couldn't reach the server. Check your connection and try again."
          action={
            <Button variant="primary" onClick={handleRetry}>
              Retry
            </Button>
          }
        />
      );
    }

    // Fix wave (review round 3): previously an initial-load failure left
    // `status: 'error'` unreachable in practice (see isFeedDisconnectError's
    // history in allArticlesFeed.ts) and fell through to the generic
    // "No articles yet"/"No articles found" EmptyState below - indistinguishable
    // from a genuinely empty feed. This gives it its own state, with a Retry
    // button that replays the exact request that failed (`handleRetry` ->
    // `lastRequestRef`).
    if (state.status === 'error' && state.articles.length === 0) {
      return (
        <EmptyState
          title="Something went wrong"
          description={state.errorMessage || "We couldn't load the articles feed. Please try again."}
          action={
            <Button variant="primary" onClick={handleRetry}>
              Retry
            </Button>
          }
        />
      );
    }

    if (state.articles.length === 0) {
      // COM-639: filters take priority over the plain search-only empty
      // state below - a zero-row reply while a filter deviates from default
      // gets its own "No results found" state per the task brief, with a
      // "Clear filters" action that resets filters ONLY (see
      // `handleClearFilters`'s own comment for why search is untouched).
      // This fires regardless of whether a search term is ALSO active - the
      // brief's trigger condition is `!isDefaultFilters()`, not "filters
      // deviate and no search" - the user can still clear the search term
      // via the search box itself.
      if (!isDefaultFilters(filters, activeSourceIds)) {
        return (
          <EmptyState
            title="No results found"
            description={
              debouncedSearch
                ? `No articles match "${debouncedSearch}" with the current filters.`
                : 'Try adjusting or clearing your filters.'
            }
            action={
              <Button variant="secondary" onClick={handleClearFilters}>
                Clear filters
              </Button>
            }
          />
        );
      }
      if (debouncedSearch) {
        return (
          <EmptyState
            title="No articles found"
            description={`No articles match "${debouncedSearch}".`}
            action={
              <Button variant="secondary" onClick={handleClearSearch}>
                Clear search
              </Button>
            }
          />
        );
      }
      return (
        <EmptyState
          title="No articles yet"
          description="Add a source to start monitoring regulatory updates."
          action={addSourceButton}
        />
      );
    }

    return (
      <>
        {/* Fix wave (review round 3): 'error' here is the exhausted
        append-error case (`MAX_CONSECUTIVE_APPEND_ERRORS` in
        allArticlesFeed.ts) - the streak's earlier failures already showed a
        toast (see AllArticlesPage's runFetch) and quietly returned to
        'ready' so the sentinel could keep retrying; once the streak is
        exhausted the reducer pins 'error' and `canFetchNextPage` refuses to
        auto-retry, so this banner is the only way back in. */}
        {(state.status === 'disconnected' || state.status === 'error') && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <Typography fontSize={14} color="error.main">
              {state.status === 'disconnected'
                ? 'Connection lost.'
                : state.errorMessage || 'Something went wrong.'}
            </Typography>
            <Button variant="secondary" onClick={handleRetry}>
              Retry
            </Button>
          </Box>
        )}
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 2 }}>
          {state.articles.map((article) => (
            <ArticleCard
              key={article.url}
              title={article.title}
              preview={article.relevance}
              date={formatFeedArticleDate(article.updateDate)}
              isNew={article.isNew}
              onViewSummary={() => handleViewSummary(article)}
            />
          ))}
        </Box>
        <div ref={sentinelRef} data-testid="all-articles-sentinel" />
        {state.status === 'loading-more' && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
            <Spinner sx={{ width: 24, height: 24 }} />
          </Box>
        )}
      </>
    );
  };

  return (
    <Box data-testid="all-articles-page" sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
      {/* COM-639: toolbar order per Figma 2871:49860 - Search, Sources,
      Date, sort, then Add source pushed to the far right. */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <SearchInput value={searchInput} onChange={setSearchInput} placeholder="Search Article" />
        <SourcesFilter
          sourceIds={filters.sourceIds}
          onChange={(sourceIds) => setFilters((prev) => ({ ...prev, sourceIds }))}
          active={sourcesFilterActive(filters, activeSourceIds)}
          onSourcesLoaded={handleSourcesLoaded}
          reloadToken={sourceScanCompletedCount}
          onOpenSourcesSettings={() => navigate('/sources')}
        />
        <DateRangePicker
          value={{ preset: filters.datePreset, range: filters.dateRange }}
          onChange={(value) => setFilters((prev) => ({ ...prev, datePreset: value.preset, dateRange: value.range }))}
          active={dateFilterActive(filters)}
        />
        <SortMenu sortBy={sort.sortBy} sortDir={sort.sortDir} onChange={(sortBy, sortDir) => setSort({ sortBy, sortDir })} />
        <Box sx={{ flex: 1 }} />
        {addSourceButton}
      </Box>

      {/* COM-640: right-aligned scanning/freshness indicator (Figma
      3142:29000 / 2871:49860) - see ScanIndicator.tsx. `data-testid` scopes
      the e2e screenshot assertions to this row alone, in both indicator
      states, rather than the whole page.
      Whole-branch review fix wave (Critical 2 + 3): `scanningSourceCount`
      replaces the old boolean `isAddSourceScanning` now that concurrent
      add-source scans are trackable at all - ScanIndicator renders the
      real count ("Scanning N sources") instead of a fixed "1". */}
      <Box
        data-testid="all-articles-heading-row"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
      >
        <Typography variant="h4">All articles</Typography>
        <ScanIndicator scanningCount={scanningSourceCount} lastUpdatedAt={state.lastUpdatedAt} />
      </Box>

      {renderBody()}

      <AddSourceDialog />

      {/* COM-641: ArticleSummaryPanel no longer renders its own close button
      (the old byte-identical-from-COM-638 version did, `position: absolute`,
      and collided with this SidePanel's own close IconButton - both shared
      `aria-label="close"`). Rebuilt on the kit, it only needs SidePanel's
      own chrome to close it, so no wrapping box or `onClose` prop either. */}
      <SidePanel open={summaryPanelOpen} title="Article summary" onClose={handleCloseSummary} width={560}>
        <ArticleSummaryPanel
          summary={summaryData}
          loading={summaryLoading}
          articleTitle={activeArticle?.title || ''}
          articleUrl={activeArticle?.url || ''}
          isNew={activeArticle?.isNew ?? false}
          onExportPdf={handleExportPdf}
        />
      </SidePanel>
    </Box>
  );
};

export default AllArticlesPage;
